<template>
    <component
        :is="tag"
        :class="[
            'block',
            sizeClass,
            variantClass,
            borderColorClass,
            elevationClass,
            hoverClass,
            isInteractive ? 'cursor-pointer' : '',
        ]"
        :type="tag === 'button' ? 'button' : null"
        @click="$emit('click', $event)"
    >
        <slot />
    </component>
</template>

<script>
    const RADIUS_CLASSES = {
        xs: 'rounded-lg',
        sm: 'rounded-lg',
        compact: 'rounded-xl',
        md: 'rounded-xl',
        lg: 'rounded-2xl',
    };
    const PADDING_CLASSES = {
        xs: 'px-3 py-2',
        sm: 'p-3',
        compact: 'p-4',
        md: 'p-5',
        lg: 'p-6',
    };

    // Cards carry an all-sides "glow" elevation tinted with their own border color (see
    // `borderColor`). Per the elevation contract (DESIGN.md: "card elevation = interactivity")
    // only clickable cards are elevated: a small resting glow on interactive cards, a medium
    // glow on hover, and a large glow for the selected/active card. Each tier pairs a
    // geometry utility (shadow-card*) with a `shadow-<token>` color utility in the computeds.
    // Non-interactive ("inactive") cards keep the white fill and real border but stay flat —
    // no shadow, hover, or cursor. `disabled` cards go further: the default variant swaps to
    // a gray-100 fill with the border melted into it, so "white + glow = tap me" stays reliable.
    const REST_SHADOW = 'shadow-card';
    const ACTIVE_SHADOW = 'shadow-card-active';
    const HOVER_SHADOW = 'hover:shadow-card-hover transition-shadow';

    // Variants don't bake elevation in. `dark` is the one exception: its shadow is part of its
    // identity (dark cards always read as elevated regardless of interactivity).
    const VARIANT_CLASSES = {
        default: 'bg-white',
        dark: 'bg-v-dark-950 border border-v-dark-950 text-white shadow-sm',
        'dark-outlined': 'bg-v-dark-950 border border-white text-white shadow-sm',
        highlight: 'bg-v-brand-50',
        flat: 'bg-white',
        // Borderless grey slab (no elevation). Reads as a soft, filled tile rather than a
        // white "tap me" card; interactive ones darken on hover. Selection is expected to
        // be shown by the content, not a ring, so `active` adds no border/glow here.
        muted: 'bg-v-dark-100',
    };

    export default {
        name: 'v-base-card',
        props: {
            active: {
                default: false,
                type: Boolean,
            },
            // Disabled cards read as muted gray slabs (gray fill, melted border) and
            // never respond to hover/click — for "coming soon" tiles, disabled options,
            // and the like. Distinct from merely non-interactive cards, which keep the
            // normal white look but carry no elevation or hover affordance.
            disabled: {
                default: false,
                type: Boolean,
            },
            // `null` (default) auto-detects from `@click` binding: shadow + cursor only
            // when a parent actually wired up a click handler. Pass `true` / `false`
            // explicitly to override (e.g. visually-elevated promo card that isn't
            // strictly clickable, or click-bound card meant to read as flat).
            interactive: {
                default: null,
                type: Boolean,
            },
            // Drop the card's inner padding (keeping its radius/border/elevation) so flush
            // content — e.g. a table whose own cells provide the padding — sits edge to edge.
            flush: {
                default: false,
                type: Boolean,
            },
            // Opt-in subtle brand gradient background (white → light brand tint) for
            // promo/marketing surfaces. Only affects the default (white) variant.
            gradient: {
                default: false,
                type: Boolean,
            },
            // Opt-in resting drop shadow for non-interactive cards — surfaces whose
            // design calls for elevation without the "tap me" hover affordance.
            elevated: {
                default: false,
                type: Boolean,
            },
            // Render as a seamless row inside a parent surface that already owns the border,
            // radius, and divided-list structure. Active embedded rows use the same quiet
            // wash + leading accent as selected ledger rows, avoiding a ring that collides
            // with the parent's border and row dividers.
            embedded: {
                default: false,
                type: Boolean,
            },
            size: {
                default: 'md',
                type: String,
                validator: v => Object.keys(RADIUS_CLASSES).includes(v),
            },
            tag: {
                default: 'div',
                type: String,
            },
            variant: {
                default: 'default',
                type: String,
                validator: v => Object.keys(VARIANT_CLASSES).includes(v),
            },
        },
        emits: ['click'],
        computed: {
            isInteractive() {
                if (this.disabled) return false;
                if (this.interactive !== null) return this.interactive;
                return Boolean(this.$attrs?.onClick);
            },
            sizeClass() {
                const padding = this.flush ? '' : PADDING_CLASSES[this.size];
                if (this.embedded) return padding;
                const radius = RADIUS_CLASSES[this.size];
                return this.flush ? radius : `${radius} ${padding}`;
            },
            variantClass() {
                // Disabled default cards trade the white fill for gray-100; dark/highlight/flat
                // keep their identity colors regardless of state.
                if (this.disabled && this.variant === 'default') return 'bg-gray-100';
                // Opt-in subtle brand gradient (white → light brand tint) for promo/marketing
                // surfaces; only applies to the default (white) variant.
                if (this.gradient && this.variant === 'default') {
                    return 'bg-gradient-to-br from-white via-white to-v-brand-50';
                }
                if (this.embedded && this.active) return 'bg-v-brand-50/50';
                return VARIANT_CLASSES[this.variant];
            },
            // Border color token, shared by the resting border and the resting glow. Active wins
            // (a dimmed themed shade), then highlight (light brand), then the default gray.
            borderColor() {
                // Active uses a soft themed shade (200) — bright enough to read as selected, but
                // close to the glow so the edge still melts into it rather than drawing a hard line.
                if (this.active) return 'indigo-200';
                if (this.variant === 'highlight') return 'v-brand-100';
                // Disabled default cards: border matches the gray-100 fill — visually melted in,
                // but kept so the box size stays identical to enabled siblings.
                if (this.disabled && this.variant === 'default') return 'gray-100';
                return 'v-dark-200';
            },
            borderColorClass() {
                if (this.embedded) {
                    return this.active ? 'shadow-[inset_3px_0_0_theme(colors.v-brand.500)]' : '';
                }
                if (['dark', 'dark-outlined', 'muted'].includes(this.variant)) return '';
                return `border border-${this.borderColor}`;
            },
            // Resting elevation: interactive cards get a small glow; the selected/active card
            // gets the largest; static cards stay flat. `flat` opts out entirely; `dark` keeps
            // its own baked-in shadow.
            // Glow tints, deliberately distinct from the border color so the elevation reads
            // well: the hover glow is a deeper tone (pale border colors barely show as a shadow),
            // and the active glow is a *muted* themed tint so the selected card lifts softly.
            hoverGlowColor() {
                if (this.variant === 'highlight') return 'v-brand-200';
                return 'v-dark-300';
            },
            activeGlowColor() {
                return 'indigo-300';
            },
            elevationClass() {
                if (this.embedded) return '';
                if (['dark', 'dark-outlined', 'flat', 'muted'].includes(this.variant)) return '';
                // Active gets the largest glow but a muted color; interactive cards a small
                // resting glow tinted to match their border; inactive/disabled cards none at all.
                if (this.active) return `${ACTIVE_SHADOW} shadow-${this.activeGlowColor}`;
                if (!this.isInteractive) {
                    return this.elevated ? `${REST_SHADOW} shadow-${this.borderColor}` : '';
                }
                return `${REST_SHADOW} shadow-${this.borderColor}`;
            },
            hoverClass() {
                if (!this.isInteractive) return '';
                if (this.embedded) {
                    return this.active
                        ? 'hover:bg-v-brand-50/70 transition-colors'
                        : 'hover:bg-v-brand-50 transition-colors';
                }
                if (this.variant === 'muted') return 'hover:bg-v-dark-200 transition-colors';
                if (['dark', 'dark-outlined'].includes(this.variant)) return 'hover:bg-v-dark-800 transition-colors';
                // Active cards already sit at the largest glow; keep only the smooth transition.
                if (this.active) return 'transition-shadow';
                return `${HOVER_SHADOW} hover:shadow-${this.hoverGlowColor}`;
            },
        },
    };
</script>
